import React, { useRef, useState, useEffect } from 'react';
import { Testimonial } from '../types';
import { Star, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';

interface TestimonialsTickerProps {
  testimonials?: Testimonial[];
}

export function TestimonialsTicker({ testimonials = [] }: TestimonialsTickerProps) {
  if (testimonials.length === 0) return null;

  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll positions to enable/disable button controls
  const handleContainerScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setCanScrollLeft(scrollLeft > 15);
      // Give some tolerance for rounding issues
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 15);
    }
  };

  // Keep checks in sync on load & when testimonials change
  useEffect(() => {
    handleContainerScroll();
    
    // Add event listener to check on window resize too
    window.addEventListener('resize', handleContainerScroll);
    return () => window.removeEventListener('resize', handleContainerScroll);
  }, [testimonials]);

  // Handle manual navigation arrow clicks
  const handleScroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = direction === 'left' ? -340 : 340;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-theme-bg overflow-hidden relative border-t border-theme-border" id="testimonials-section">
      {/* Hide default scrollbars for elegant pure layout */}
      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}} />

      <div className="max-w-7xl mx-auto px-6 mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-3 py-1 border border-theme-accent/20 bg-theme-accent/5 mb-4 text-theme-accent uppercase tracking-[0.2em] text-[10px] font-mono"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Témoignages de mariés</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-3xl md:text-5xl font-serif text-theme-text tracking-tight font-light"
          >
            Leurs émotions en quelques mots
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-sm text-theme-secondary mt-3 max-w-xl font-sans font-light"
          >
            Découvrez les précieux retours des couples d'exception qui nous ont fait confiance.
          </motion.p>
        </div>

        {/* Dynamic Controls with guidance */}
        <div className="flex items-center gap-5 self-start md:self-end shrink-0 select-none">
          <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 hidden sm:inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
            Glissez avec le doigt de gauche à droite
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleScroll('left')}
              className={`p-2.5 border transition-all rounded-none cursor-pointer ${
                canScrollLeft 
                  ? 'border-theme-border text-theme-text hover:border-theme-accent hover:text-theme-accent bg-theme-card' 
                  : 'border-theme-border/20 text-zinc-700 bg-transparent opacity-40 cursor-not-allowed'
              }`}
              disabled={!canScrollLeft}
              title="Précédent"
            >
              <ChevronLeft className="w-4.5 h-4.5" />
            </button>
            <button
              onClick={() => handleScroll('right')}
              className={`p-2.5 border transition-all rounded-none cursor-pointer ${
                canScrollRight 
                  ? 'border-theme-border text-theme-text hover:border-theme-accent hover:text-theme-accent bg-theme-card' 
                  : 'border-theme-border/20 text-zinc-700 bg-transparent opacity-40 cursor-not-allowed'
              }`}
              disabled={!canScrollRight}
              title="Suivant"
            >
              <ChevronRight className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Swipeable Viewport */}
      <div className="relative w-full overflow-hidden py-4">
        {/* Soft fading edges for professional luxury feel - optimized widths */}
        <div className="absolute inset-y-0 left-0 w-8 sm:w-24 md:w-48 bg-gradient-to-r from-theme-bg to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-8 sm:w-24 md:w-48 bg-gradient-to-l from-theme-bg to-transparent z-10 pointer-events-none" />

        <div
          ref={containerRef}
          onScroll={handleContainerScroll}
          className="overflow-x-auto scroll-smooth snap-x snap-mandatory flex gap-4 sm:gap-6 px-6 sm:px-12 md:px-24 py-4 no-scrollbar"
        >
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="w-[285px] sm:w-[360px] md:w-[420px] shrink-0 bg-theme-card border border-theme-border hover:border-theme-accent/30 p-6 md:p-8 flex flex-col justify-between transition-all duration-300 relative group snap-start select-none"
            >
              {/* Subtle top ambient accent line on hover */}
              <div className="absolute top-0 left-0 w-0 h-[1px] bg-theme-accent group-hover:w-full transition-all duration-500" />
              
              <div>
                {/* Gold Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < testimonial.rating
                          ? 'fill-theme-accent text-theme-accent'
                          : 'text-zinc-700'
                      }`}
                    />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-theme-text text-xs sm:text-sm md:text-base font-light font-sans italic leading-relaxed mb-6 select-text text-zinc-300">
                  "{testimonial.content}"
                </p>
              </div>

              {/* Author & Context info */}
              <div className="border-t border-theme-border/50 pt-4 flex items-center justify-between">
                <div>
                  <h4 className="font-serif text-xs sm:text-sm font-medium text-theme-text group-hover:text-theme-accent transition-colors">
                    {testimonial.author}
                  </h4>
                  {testimonial.role && (
                    <p className="text-[9px] sm:text-[10px] font-mono tracking-wider text-theme-secondary uppercase mt-0.5">
                      {testimonial.role}
                    </p>
                  )}
                </div>
                <span className="text-[9px] sm:text-[10px] font-mono text-zinc-600 bg-zinc-900/50 px-2 py-0.5 border border-zinc-800">
                  {testimonial.date}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
